/* Loaded on product pages only. Uses the existing Ghost tabs component. */
(() => {
  if (window.ghostReviewScrollInstalled) return;
  window.ghostReviewScrollInstalled = true;

  const REVIEW_TAB_LABEL = 'Reviews';
  // Set a number here to override automatic header clearance, e.g. 120.
  const HEADER_OFFSET = null;
  const normalise = value => String(value || '').trim().toLowerCase();
  const excluded = 'product-card, .product-media-overlay, dialog, ghost-content-tabs';
  const enhanced = new Set();

  function findTarget() {
    for (const panel of document.querySelectorAll('ghost-content-tabs .ghost-tabs__panel')) {
      if (panel.closest('product-card, .product-media-overlay, dialog')) continue;
      if (normalise(panel.dataset.heading) !== normalise(REVIEW_TAB_LABEL)) continue;
      const tabs = panel.closest('ghost-content-tabs');
      if (typeof tabs.activate === 'function') return { panel, tabs };
    }
    return null;
  }

  function clearance() {
    if (Number.isFinite(HEADER_OFFSET)) return HEADER_OFFSET;
    // Horizon's header height remains useful when its sticky header is offscreen.
    const root = getComputedStyle(document.documentElement);
    let height = Math.max(0, parseFloat(root.getPropertyValue('--header-height')) || 0);
    for (const header of document.querySelectorAll('header-component, #header-group')) {
      height = Math.max(height, header.getBoundingClientRect().height);
    }
    return height + 20;
  }

  function openReviews() {
    const target = findTarget();
    if (!target) return;
    const { tabs, panel } = target;
    tabs.build();
    const index = tabs.panels.indexOf(panel);
    if (index < 0) return;
    // activate opens the accordion explicitly; repeated clicks never close it.
    tabs.activate(index);
    requestAnimationFrame(() => {
      if (!panel.isConnected) return;
      const mobile = tabs.media.matches;
      const control = mobile ? tabs.accordionButtons[index] : tabs.buttons[index];
      const destination = mobile ? control.parentElement : tabs.navigation;
      control.focus({ preventScroll: true });
      // Reveal the tab horizontally without scrolling the entire page first.
      if (!mobile && tabs.navigation) {
        const nav = tabs.navigation;
        const box = control.getBoundingClientRect();
        const viewport = nav.getBoundingClientRect();
        if (box.left < viewport.left) nav.scrollLeft -= viewport.left - box.left;
        else if (box.right > viewport.right) nav.scrollLeft += box.right - viewport.right;
      }
      // Allow the opened tab/accordion to finish updating its layout.
      requestAnimationFrame(() => {
        destination.style.scrollMarginTop = `${clearance()}px`;

        destination.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      });
    });
  }

  function refresh() {
    const target = findTarget();
    for (const node of enhanced) {
      if (!node.isConnected) { enhanced.delete(node); continue; }
      if (!target || node.closest(excluded)) {
        node.removeAttribute('role');
        node.removeAttribute('tabindex');
        node.removeAttribute('aria-label');
        node.removeAttribute('aria-controls');
        node.removeAttribute('data-ghost-review-link');
        enhanced.delete(node);
      }
    }
    if (!target) return;
    for (const node of document.querySelectorAll('ghost-product-rating [data-ghost-rating-content]')) {
      if (node.closest(excluded) || node.closest('a, button')) continue;
      node.dataset.ghostReviewLink = '';
      node.setAttribute('role', 'button');
      node.setAttribute('tabindex', '0');
      node.setAttribute('aria-controls', target.panel.id);
      const rating = node.querySelector('.rating')?.getAttribute('aria-label') || '';
      const count = node.querySelector('.rating-count')?.textContent.trim() || '';
      node.setAttribute('aria-label', [rating, count, 'Read reviews'].filter(Boolean).join('. '));
      enhanced.add(node);
    }
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-ghost-review-link]');
    if (!link || link.closest(excluded)) return;
    event.preventDefault();
    openReviews();
  });
  document.addEventListener('keydown', event => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-ghost-review-link]')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (!event.repeat) openReviews();
  });

  const style = document.createElement('style');
  style.textContent = '[data-ghost-review-link]{cursor:pointer}[data-ghost-review-link]:focus-visible{outline:2px solid currentColor;outline-offset:4px}';
  document.head.append(style);
  let frame;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(refresh);
  };
  // Rebind after product changes or theme-editor section replacement.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  customElements.whenDefined('ghost-content-tabs').then(schedule);
  schedule();
})();
